import { ArrowDown, Check, ChevronDown, Copy, History, Languages, Maximize2, Minimize2, Pencil, RotateCcw, SlidersHorizontal, SquarePen, ThumbsDown, ThumbsUp, Trash2, X } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'

import { forwardRef, useEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Spinner } from '@/components/ui/spinner'
import { AssistantMessage } from '@/types/assistant'

import { AssistantComposer, AssistantMark } from './AssistantComposer'
import { AssistantCardView, AssistantMarkdown } from './AssistantContent'
import { assistantText, PAGE_SUGGESTION_TOOLS, ROUTE_PATHS, stepLabel, SUGGESTIONS } from './assistantText'
import { useAssistant } from './AssistantProvider'
import { useSmoothText } from './useSmoothText'

/** The tool steps of a reply: live while working, then folded into one line that opens on click. */
const StepTrail = ({ message }: { message: AssistantMessage }) => {
  const { language } = useAssistant()
  const t = assistantText(language)
  const [open, setOpen] = useState(false)
  const working = message.streaming && !message.error
  const current = [...message.steps].reverse().find((s) => !s.done)
  if (!message.steps.length && !working) return null

  if (working) {
    return (
      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <Spinner className="size-3" />
        <span className="assistant-shimmer">{current ? stepLabel(language, current.name) : t.working}</span>
      </p>
    )
  }
  return (
    <div className="text-xs text-muted-foreground">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-1 hover:text-ink">
        <Check className="size-3" /> {t.stepsDone(message.steps.length)}
        <ChevronDown className={cn('size-3 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <ul className="mt-1 space-y-0.5 border-l border-border-subtle pl-3">
          {message.steps.map((step, i) => (
            <li key={i} className={cn(step.ok === false && 'text-destructive')}>{stepLabel(language, step.name)}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

const ModelMessage = ({ message }: { message: AssistantMessage }) => {
  const { language, rate, retry, openReview } = useAssistant()
  const navigate = useNavigate()
  const t = assistantText(language)
  const [copied, setCopied] = useState(false)
  const smooth = useSmoothText(message.content, Boolean(message.live))
  // Cards, changes and actions follow once the words have finished appearing.
  const settled = !message.streaming && smooth.done

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message.content)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch (error) {
      console.warn('Copy failed', error)
    }
  }

  return (
    <div className="space-y-3">
      <StepTrail message={message} />

      {smooth.text && (
        <div className={cn('text-body1 leading-relaxed text-ink', message.refused && 'text-muted-foreground', language === 'bn' && 'font-bn')}>
          <AssistantMarkdown text={smooth.text} />
          {!settled && <span className="assistant-caret" aria-hidden />}
        </div>
      )}

      {(settled || !message.live) && (
        <div className="assistant-fade-in space-y-3">
          {message.cards.map((card, i) => (
            <AssistantCardView key={i} card={card} onRoute={(routeId) => ROUTE_PATHS[routeId] && navigate(ROUTE_PATHS[routeId])} />
          ))}

          {message.proposals.length > 0 && (
            <div className="rounded-xl border border-border bg-card p-3 shadow-card">
              <div className="flex items-center justify-between border-b border-border-subtle pb-2">
                <p className="text-heading-md">{t.storeChanges}</p>
              </div>
              {message.proposals.map((p) => (
                <div key={p.proposal_uid} className="flex items-center gap-3 pt-3">
                  <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-subdued"><Pencil className="size-4" /></span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{p.title}</p>
                    <span className="rounded-sm bg-violet-lighter px-1.5 py-0.5 text-xs text-violet-dark">{t.changes(p.change_count)}</span>
                  </div>
                  {p.status === 'published' && <span className="flex items-center gap-1 text-xs text-success-foreground"><Check className="size-3.5" />{t.published}</span>}
                  {p.status === 'discarded' && <span className="text-xs text-muted-foreground">{t.discarded}</span>}
                  {(!p.status || p.status === 'pending') && (
                    <Button variant="secondary" size="sm" onClick={() => openReview(p)}>{t.review}</Button>
                  )}
                </div>
              ))}
            </div>
          )}

          {message.refused && (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">{t.refusedTry}</p>
              <AssistantSuggestions limit={3} />
            </div>
          )}

          {message.error && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <span className="flex-1">{message.error}</span>
              <Button variant="outline" size="sm" onClick={retry} className="gap-1"><RotateCcw /> {t.retry}</Button>
            </div>
          )}

          {message.content && typeof message.id === 'number' && !message.refused && (
            <div className="flex items-center gap-0.5 text-muted-foreground">
              <Button variant="ghost" size="icon-sm" onClick={copy} aria-label={copied ? t.copied : t.copy}>{copied ? <Check /> : <Copy />}</Button>
              <Button variant="ghost" size="icon-sm" onClick={() => rate(message.id as number, 1)} aria-label={t.helpful} className={cn(message.rating === 1 && 'text-ink')}><ThumbsUp /></Button>
              <Button variant="ghost" size="icon-sm" onClick={() => rate(message.id as number, -1)} aria-label={t.notHelpful} className={cn(message.rating === -1 && 'text-ink')}><ThumbsDown /></Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** Starter prompts the staff member can actually use. */
export const AssistantSuggestions = ({ className, limit = 6 }: { className?: string; limit?: number }) => {
  const { language, capabilities, send } = useAssistant()
  const { pathname } = useLocation()
  // Prompts about the current screen first (e.g. product ideas on Products), then the rest.
  const here = PAGE_SUGGESTION_TOOLS.find(([prefix]) => pathname.startsWith(prefix))?.[1] ?? []
  const items = SUGGESTIONS
    .filter((s) => capabilities?.tools.includes(s.tool))
    .sort((a, b) => Number(here.includes(b.tool)) - Number(here.includes(a.tool)))
    .slice(0, limit)
  if (!items.length) return null
  return (
    <ul className={cn('space-y-1', className)}>
      {items.map((s) => (
        <li key={s.tool}>
          <button
            type="button"
            onClick={() => send(s[language])}
            className={cn('flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-left text-sm text-ink hover:bg-subdued', language === 'bn' && 'font-bn')}
          >
            <span className="text-muted-foreground">→</span> {s[language]}
          </button>
        </li>
      ))}
    </ul>
  )
}

export const RecentsList = ({ onPick }: { onPick: () => void }) => {
  const { language, recents, loadRecents, openConversation, deleteConversation } = useAssistant()
  const t = assistantText(language)
  useEffect(loadRecents, [loadRecents])
  if (!recents.length) return <p className="px-2 py-3 text-sm text-muted-foreground">{t.noRecents}</p>
  return (
    <ul className="space-y-0.5">
      {recents.map((c) => (
        <li key={c.conversation_uid} className="group flex items-center rounded-md hover:bg-subdued">
          <button type="button" onClick={() => { openConversation(c.conversation_uid); onPick() }} className="min-w-0 flex-1 truncate px-2 py-1.5 text-left text-sm">
            {c.title}
          </button>
          <Button variant="ghost" size="icon-sm" className="opacity-0 group-hover:opacity-100" onClick={() => deleteConversation(c.conversation_uid)} aria-label={t.deleteChat}>
            <Trash2 />
          </Button>
        </li>
      ))}
    </ul>
  )
}

/** Round outlined icon button used in the panel header (forwards its ref, so it can be a menu trigger). */
const RoundButton = forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement> & { label: string }>(
  ({ label, className, children, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      className={cn('grid size-9 place-items-center rounded-full border border-border-subtle bg-card text-ink shadow-z1 outline-none transition-colors hover:bg-subdued focus-visible:ring-2 focus-visible:ring-ring [&_svg]:size-4', className)}
      {...props}
    >
      {children}
    </button>
  ),
)
RoundButton.displayName = 'RoundButton'

/**
 * The conversation panel: its own rounded white panel inside the black frame, on the right of every admin page
 * (the page narrows beside it on desktop; full screen on phones).
 */
export const AssistantPanel = () => {
  const { isOpen, isExpanded, panelWidth, setPanelWidth, language, messages, close, toggleExpanded, newChat, setLanguage } = useAssistant()
  const [resizing, setResizing] = useState(false)

  // Drag the left edge to resize; the panel is anchored right, so width = distance from the pointer to the right edge.
  const startResize = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    const handle = e.currentTarget
    handle.setPointerCapture(e.pointerId)
    setResizing(true)
    // Page padding normally animates; while dragging it must follow the pointer exactly.
    document.body.dataset.assistantResizing = 'true'
    const move = (ev: PointerEvent) => setPanelWidth(window.innerWidth - ev.clientX - 4)
    const end = () => {
      setResizing(false)
      delete document.body.dataset.assistantResizing
      handle.removeEventListener('pointermove', move)
      handle.removeEventListener('pointerup', end)
      handle.removeEventListener('pointercancel', end)
    }
    handle.addEventListener('pointermove', move)
    handle.addEventListener('pointerup', end)
    handle.addEventListener('pointercancel', end)
  }
  const resizeWithKeys = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft') setPanelWidth(panelWidth + 24)
    else if (e.key === 'ArrowRight') setPanelWidth(panelWidth - 24)
    else return
    e.preventDefault()
  }
  const t = assistantText(language)
  const [showRecents, setShowRecents] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  // Follow the reply while the reader is at the bottom; once they scroll up, stay put and offer a jump back.
  const pinned = useRef(true)
  const [showJump, setShowJump] = useState(false)

  const toBottom = (smooth = false) => {
    const el = scrollRef.current
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' })
  }
  const onScroll = () => {
    const el = scrollRef.current
    if (!el) return
    pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
    setShowJump(!pinned.current)
  }

  // Sending a message always returns to the bottom.
  const userCount = messages.filter((m) => m.role === 'user').length
  useEffect(() => {
    pinned.current = true
    setShowJump(false)
    toBottom()
  }, [userCount])

  // The reply grows frame by frame; keep the bottom in view while pinned.
  const hasMessages = messages.length > 0
  useEffect(() => {
    const content = contentRef.current
    if (!content) return
    const observer = new ResizeObserver(() => pinned.current && toBottom())
    observer.observe(content)
    return () => observer.disconnect()
  }, [isOpen, showRecents, hasMessages])

  if (!isOpen) return null

  return (
    <aside
      aria-label={t.name}
      className={cn(
        'no-print fixed inset-0 z-50 flex flex-col bg-card lg:inset-y-0 lg:left-auto lg:right-0 lg:z-40 lg:w-[var(--assistant-width)] lg:border-l lg:border-border',
        resizing && 'select-none',
      )}
      style={{ '--assistant-width': `${panelWidth}px` } as React.CSSProperties}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={t.resize}
        aria-valuenow={panelWidth}
        tabIndex={0}
        onPointerDown={startResize}
        onDoubleClick={toggleExpanded}
        onKeyDown={resizeWithKeys}
        className="group absolute inset-y-0 -left-2 z-10 hidden w-3 cursor-col-resize touch-none outline-none lg:block"
      >
        <span className={cn('absolute inset-y-1/3 left-1/2 w-1 -translate-x-1/2 rounded-full bg-sidebar-muted opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100', resizing && 'opacity-100')} />
      </div>
      <header className="flex shrink-0 items-center gap-2 p-3">
        <button
          type="button"
          onClick={() => setShowRecents((v) => !v)}
          className={cn(
            'flex h-9 items-center gap-1.5 rounded-full border border-border-subtle px-3.5 text-sm font-medium shadow-z1 outline-none transition-colors hover:bg-subdued focus-visible:ring-2 focus-visible:ring-ring',
            showRecents ? 'bg-subdued' : 'bg-card',
          )}
        >
          <History className="size-4" /> {t.recents}
        </button>
        <div className="flex-1" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <RoundButton label={language === 'bn' ? 'সেটিংস' : 'Settings'}><SlidersHorizontal /></RoundButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => { newChat(); setShowRecents(false) }}><SquarePen className="mr-2 size-4" /> {t.newChat}</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => setLanguage('en')}><Languages className="mr-2 size-4" /> English {language === 'en' && '✓'}</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setLanguage('bn')}><Languages className="mr-2 size-4" /> বাংলা {language === 'bn' && '✓'}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <RoundButton label={isExpanded ? t.collapse : t.expand} onClick={toggleExpanded} className="hidden lg:grid">
          {isExpanded ? <Minimize2 /> : <Maximize2 />}
        </RoundButton>
        <RoundButton label={t.close} onClick={close}><X /></RoundButton>
      </header>

      <div className="relative flex min-h-0 flex-1 flex-col">
      <div ref={scrollRef} onScroll={onScroll} className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-4 pb-4">
        {showRecents ? (
          <RecentsList onPick={() => setShowRecents(false)} />
        ) : messages.length === 0 ? (
          <div className="m-auto flex flex-col items-center gap-4 py-10 text-center">
            <AssistantMark className="size-12 [&_svg]:size-6" />
            <p className={cn('text-heading-xl', language === 'bn' && 'font-bn')}>{t.emptyTitle}</p>
            <AssistantSuggestions className="w-full max-w-[320px] text-left" />
          </div>
        ) : (
          <div ref={contentRef} className={cn('mx-auto w-full space-y-5 pt-2', isExpanded && 'max-w-[600px]')}>
            {messages.map((m) => (m.role === 'user' ? (
              <div key={m.id} className="assistant-fade-in flex justify-end">
                <p className={cn('max-w-[85%] whitespace-pre-wrap rounded-2xl bg-fill px-3.5 py-2 text-body1', language === 'bn' && 'font-bn')}>{m.content}</p>
              </div>
            ) : (
              <ModelMessage key={m.id} message={m} />
            )))}
          </div>
        )}
      </div>
      {showJump && !showRecents && messages.length > 0 && (
        <button
          type="button"
          onClick={() => { pinned.current = true; setShowJump(false); toBottom(true) }}
          aria-label={t.jumpToLatest}
          className="assistant-fade-in absolute bottom-3 left-1/2 grid size-8 -translate-x-1/2 place-items-center rounded-full border border-border-subtle bg-card text-ink shadow-dropdown hover:bg-subdued"
        >
          <ArrowDown className="size-4" />
        </button>
      )}
      </div>

      <div className="shrink-0 p-3 pt-0">
        <AssistantComposer variant="panel" autoFocus />
      </div>
    </aside>
  )
}
